import { AppstoreAddOutlined, HolderOutlined } from "@ant-design/icons"
import { Button, Flex, Modal } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeSize, homeText } from "../../home-theme.ts"

interface EditLayoutBarProps {
  /** The view being customised, named so the user knows what they are changing. */
  viewName?: string | null
  saving: boolean
  /** True once the draft differs from the layout editing began with. */
  dirty: boolean
  onAddWidget: () => void
  onResetToDefault: () => void
  onCancel: () => void
  onSave: () => void
}

/**
 * The one place every layout-editing control lives.
 *
 * Before this, Save / Cancel / Restore Default / Add Tile sat in the view
 * dropdown's own row as `size="small"` buttons, and Remove appeared again on
 * every card — four different places to look for "how do I finish". The bar
 * is sticky so those controls stay reachable down a long dashboard, and it
 * carries the pale brand wash that is this surface's only "you are editing"
 * signal.
 */
export const EditLayoutBar = ({
  viewName,
  saving,
  dirty,
  onAddWidget,
  onResetToDefault,
  onCancel,
  onSave
}: EditLayoutBarProps) => {
  const { t } = useTranslation()

  // Reset replaces the whole draft, so unsaved work is confirmed away rather
  // than silently discarded. With a clean draft there is nothing to warn about.
  const handleReset = () => {
    if (!dirty) {
      onResetToDefault()
      return
    }
    Modal.confirm({
      title: t("portfolio.editLayout.resetTitle", "Reset to default?"),
      content: t(
        "portfolio.editLayout.resetConfirm",
        "This replaces your unsaved changes with the default layout. Nothing is saved until you choose Save layout."
      ),
      okText: t("portfolio.editLayout.resetConfirmOk", "Reset draft"),
      cancelText: t("portfolio.editLayout.keepEditing", "Keep editing"),
      onOk: onResetToDefault
    })
  }

  return (
    <div
      data-testid="edit-layout-bar"
      data-guide-target="dashboard-edit-layout-bar"
      className="home-edit-bar"
      style={{
        background: homeColor.editBg,
        border: `1px solid ${homeColor.editBorder}`,
        borderRadius: homeSize.cardRadius
      }}
    >
      <Flex
        align="center"
        gap={16}
        wrap="wrap"
        style={{ padding: "12px 16px" }}
      >
        <Flex
          align="center"
          gap={12}
          style={{ minWidth: 0, flex: "1 1 320px" }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 8,
              flexShrink: 0,
              background: homeColor.surface,
              border: `1px solid ${homeColor.editBorder}`,
              color: homeColor.teal
            }}
          >
            <HolderOutlined />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...homeText.actionTitle, color: homeColor.ink }}>
              {t("portfolio.editLayout.barTitle", "Customize your dashboard")}
            </div>
            <div style={{ ...homeText.meta, color: homeColor.inkMuted }}>
              {viewName
                ? t(
                    "portfolio.editLayout.barHintNamed",
                    "Drag widgets to reorder. Changes apply to {{view}}.",
                    { view: viewName }
                  )
                : t(
                    "portfolio.editLayout.barHint",
                    "Drag widgets to reorder. Changes apply to this view."
                  )}
            </div>
          </div>
        </Flex>

        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          className="home-edit-bar-actions"
        >
          <Button
            icon={<AppstoreAddOutlined />}
            onClick={onAddWidget}
            disabled={saving}
            data-guide-target="dashboard-add-tile-button"
          >
            {t("portfolio.catalog.addWidget", "Widget")}
          </Button>
          <Button type="text" onClick={handleReset} disabled={saving}>
            {t("portfolio.editLayout.reset", "Reset to default")}
          </Button>
          <Button onClick={onCancel} disabled={saving}>
            {t("portfolio.editLayout.cancel", "Cancel")}
          </Button>
          <Button type="primary" loading={saving} onClick={onSave}>
            {t("portfolio.editLayout.saveLayout", "Save layout")}
          </Button>
        </Flex>
      </Flex>
    </div>
  )
}
